// src/admin/AdminOrders.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Search, ChevronDown, ChevronUp, Link as LinkIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

const STATUSES  = ['confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];
const STATUS_ST = {
  confirmed:  { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Confirmed'  },
  processing: { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'Processing' },
  shipped:    { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Shipped'    },
  delivered:  { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Delivered'  },
  cancelled:  { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Cancelled'  },
};

function OrderRow({ order, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(false);
  const st   = STATUS_ST[order.status] || STATUS_ST.confirmed;
  const date = order.createdAt?.toDate?.()?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) || '—';

  const updateStatus = async (s) => {
    setUpdating(true);
    try {
      await updateDoc(doc(db, 'orders', order.id), { status: s, trackingStatus: s.charAt(0).toUpperCase() + s.slice(1) });
      onChange(order.id, s);
      toast.success(`Order marked as ${s}`);
    } catch { toast.error('Update failed'); }
    finally { setUpdating(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}>
        <div className="flex -space-x-2 shrink-0">
          {order.items?.slice(0, 2).map((item, i) => (
            <img key={i} src={item.image} alt="" className="w-10 h-10 rounded-xl object-cover border-2 border-white" />
          ))}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-body font-bold text-gray-900 text-sm">#{order.id.slice(-8).toUpperCase()}</p>
          <p className="text-gray-400 font-body text-xs">{date} · {order.shippingAddress?.name} · {order.userEmail}</p>
        </div>
        <p className="font-display font-bold text-gray-900">₹{order.total?.toLocaleString('en-IN')}</p>
        <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${st.bg} ${st.text}`}>{st.label}</span>
        {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </div>

      {expanded && (
        <div className="border-t border-gray-100 p-5 space-y-4">
          {/* Items */}
          <div className="space-y-2">
            {order.items?.map((item, i) => (
              <div key={i} className="flex gap-3 items-center bg-gray-50 rounded-xl p-3">
                <img src={item.image} alt={item.name} className="w-12 h-12 rounded-xl object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-body font-medium text-gray-900 text-sm line-clamp-1">{item.name}</p>
                  <p className="text-xs text-gray-400 font-body">Qty: {item.quantity} × ₹{item.price?.toLocaleString('en-IN')}</p>
                </div>
                <p className="font-body font-bold text-sm">₹{(item.price * item.quantity).toLocaleString('en-IN')}</p>
              </div>
            ))}
          </div>

          {/* Details grid */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs font-body font-semibold text-gray-500 uppercase tracking-wider mb-2">Customer</p>
              <p className="font-body font-medium text-gray-900 text-sm">{order.shippingAddress?.name}</p>
              <p className="font-body text-gray-500 text-xs">{order.userEmail}</p>
              <p className="font-body text-gray-500 text-xs">{order.shippingAddress?.phone}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs font-body font-semibold text-gray-500 uppercase tracking-wider mb-2">Delivery Address</p>
              <p className="font-body text-gray-700 text-xs leading-relaxed">
                {order.shippingAddress?.address},<br />
                {order.shippingAddress?.city}, {order.shippingAddress?.state} — {order.shippingAddress?.pincode}
              </p>
            </div>
          </div>

          {/* Payment & tracking */}
          <div className="flex flex-wrap gap-4 bg-gray-50 rounded-xl p-3">
            <div><p className="text-xs text-gray-400">Payment ID</p><p className="font-mono text-xs text-gray-600">{order.paymentId || '—'}</p></div>
            <div><p className="text-xs text-gray-400">AWB Code</p><p className="font-body text-xs font-semibold text-gray-700">{order.awbCode || '—'}</p></div>
            <div><p className="text-xs text-gray-400">Courier</p><p className="font-body text-xs font-semibold text-gray-700">{order.courierName || '—'}</p></div>
            <div>
              <p className="text-xs text-gray-400">Track</p>
              <Link to={`/track/${order.id}`} target="_blank"
                className="font-body text-xs text-rose-500 hover:underline flex items-center gap-1">
                View <LinkIcon size={10} />
              </Link>
            </div>
          </div>

          {/* Status update */}
          <div>
            <p className="font-body font-semibold text-gray-700 text-sm mb-2">Update Status</p>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => {
                const sst = STATUS_ST[s];
                const cur = order.status === s;
                return (
                  <button key={s} onClick={() => !cur && updateStatus(s)} disabled={updating || cur}
                    className={`px-4 py-2 rounded-xl text-xs font-body font-bold transition-all ${
                      cur ? `${sst.bg} ${sst.text} ring-2 ring-offset-1 ring-current` : 'bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50'
                    }`}>
                    {sst.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminOrders() {
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [filter,  setFilter]  = useState('all');

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const snap = await getDocs(collection(db, 'orders'));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
        setOrders(list);
      } catch { toast.error('Failed to load'); }
      finally { setLoading(false); }
    };
    fetchOrders();
  }, []);

  const onChange = (id, status) => setOrders((p) => p.map((o) => o.id === id ? { ...o, status } : o));

  const filtered = orders.filter((o) => {
    const ms = !search || o.id.toLowerCase().includes(search.toLowerCase()) ||
      o.shippingAddress?.name?.toLowerCase().includes(search.toLowerCase()) ||
      o.userEmail?.toLowerCase().includes(search.toLowerCase());
    const mf = filter === 'all' || o.status === filter;
    return ms && mf;
  });

  const counts = orders.reduce((a, o) => { a[o.status || 'confirmed'] = (a[o.status || 'confirmed'] || 0) + 1; return a; }, {});
  const revenue = orders.reduce((s, o) => s + (o.total || 0), 0);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-xl text-gray-900">All Orders</h2>
          <p className="text-gray-400 font-body text-sm">
            {orders.length} orders · Total revenue: <span className="text-green-600 font-semibold">₹{revenue.toLocaleString('en-IN')}</span>
          </p>
        </div>
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2">
        {[{ key: 'all', label: 'All', count: orders.length }, ...STATUSES.map((s) => ({ key: s, label: STATUS_ST[s].label, count: counts[s] || 0 }))].map(({ key, label, count }) => (
          <button key={key} onClick={() => setFilter(key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-body font-medium transition-all ${
              filter === key ? 'bg-rose-500 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-rose-300'
            }`}>
            {label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${filter === key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>{count}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by order ID, customer name or email..."
          className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:border-rose-400 font-body text-sm" />
      </div>

      {/* Orders */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-10 h-10 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center text-gray-400 font-body">No orders found</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((order) => <OrderRow key={order.id} order={order} onChange={onChange} />)}
        </div>
      )}
    </div>
  );
}
